import { useMemo } from 'react';
import type { API } from '@/api';
import { usePrivateRoomTimeBarClasses } from '@/hooks/usePrivateRoomTimeBarClasses';

type TimeSlot =
  API['GET']['PrivateRoomAvailableTimeSlots']['response']['spaces'][number]['time_slots'][number];

/**
 * 指定されたスロットから一番近い有効なスロットを探索する
 * @param slot 選択されたスロット
 * @param timeSlots 全スロットの配列
 * @returns 一番近い有効なスロット、見つからない場合はundefined
 */
const findNearestAvailableSlot = (
  slot: TimeSlot,
  timeSlots: TimeSlot[],
): TimeSlot | undefined => {
  const currentIndex = timeSlots.findIndex((s) => s.time === slot.time);
  if (currentIndex === -1) return undefined;

  // 前後に同時に探索して、一番近い有効なスロットを見つける
  let leftIndex = currentIndex - 1;
  let rightIndex = currentIndex + 1;

  while (leftIndex >= 0 || rightIndex < timeSlots.length) {
    if (rightIndex < timeSlots.length) {
      const rightSlot = timeSlots[rightIndex];
      if (rightSlot.available && rightSlot.displayable) {
        return rightSlot;
      }
      rightIndex++;
    }

    if (leftIndex >= 0) {
      const leftSlot = timeSlots[leftIndex];
      if (leftSlot.available && leftSlot.displayable) {
        return leftSlot;
      }
      leftIndex--;
    }
  }

  return undefined;
};

interface BoothTimebarProps {
  name: string;
  timeSlots: TimeSlot[];
  selectedStartHHmm?: `${string}:${string}`;
  selectedDurationMinutes?: number;
  handleSlotSelect?: (time: `${string}:${string}`) => void;
  onBoothClick?: () => void;
}

export const ReservationFormPrivateRoomTimeBar = ({
  name,
  timeSlots,
  handleSlotSelect,
  selectedStartHHmm,
  selectedDurationMinutes,
  onBoothClick,
}: BoothTimebarProps) => {
  const { getSlotClasses } = usePrivateRoomTimeBarClasses(
    timeSlots,
    selectedStartHHmm,
    selectedDurationMinutes,
  );

  // スロット1つあたりの幅（%）
  const slotWidthPercent = 100 / timeSlots.length;

  // :00スロットのインデックスからラベルデータを生成
  const hourLabelData = useMemo(() => {
    const data: { hour: number; percentPosition: number }[] = [];
    const totalSlots = timeSlots.length;

    timeSlots.forEach((slot, idx) => {
      const [hours, minutes] = slot.time.split(':').map(Number);
      if (minutes === 0) {
        data.push({ hour: hours, percentPosition: (idx / totalSlots) * 100 });
      }
    });

    // 最後のスロットの次の時間を右端に追加
    if (timeSlots.length > 0) {
      const lastSlot = timeSlots[timeSlots.length - 1];
      const [lastHour] = lastSlot.time.split(':').map(Number);
      const nextHour = lastHour + 1;
      if (!data.some((d) => d.hour === nextHour)) {
        data.push({ hour: nextHour, percentPosition: 100 });
      }
    }

    return data;
  }, [timeSlots]);

  // スロットがない場合は何も表示しない
  if (timeSlots.length === 0) {
    return null;
  }

  // ブース未選択 かつ 空きスロットあり の場合のみ pointer
  const boothCursor =
    !selectedStartHHmm &&
    timeSlots.some((slot) => slot.available && slot.displayable)
      ? 'pointer'
      : 'default';

  return (
    <div
      className="booth-picker__booth"
      onClick={onBoothClick}
      style={{ cursor: boothCursor }}
    >
      <div className="booth-picker__name">{name}</div>
      <div className="booth-picker__availability">
        <div className="timebar">
          <div
            className="timebar__labels"
            style={{ position: 'relative', height: '16px' }}
          >
            {hourLabelData.map(({ hour, percentPosition }) => (
              <span
                key={hour}
                style={{
                  position: 'absolute',
                  left: `${percentPosition}%`,
                  transform: 'translateX(-50%)',
                }}
              >
                {hour}
              </span>
            ))}
          </div>
          <div className="timebar__track">
            {timeSlots.map((slot, idx) => (
              <div
                key={idx}
                className={getSlotClasses(slot, idx)}
                style={{
                  width: `${slotWidthPercent}%`,
                  cursor:
                    slot.available && slot.displayable
                      ? 'pointer'
                      : 'not-allowed',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (slot.available && slot.displayable) {
                    handleSlotSelect?.(slot.time as `${string}:${string}`);
                  } else {
                    const nearestSlot = findNearestAvailableSlot(
                      slot,
                      timeSlots,
                    );
                    if (nearestSlot) {
                      handleSlotSelect?.(
                        nearestSlot.time as `${string}:${string}`,
                      );
                    }
                  }
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
